import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { RevelDialogComponent } from './revel-dialog.component';
import { Revel } from '../../models/revel';
import { RevelService } from '../../services/revel.service';
import { AuthService } from '../../services/auth.service';
import { ResponsiveService } from '../../core/responsive.service';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-revels',
  templateUrl: './revels.component.html',
  styleUrls: ['./revels.component.scss'], // Updated to .scss
  standalone: false
})
export class RevelsComponent implements OnInit, OnDestroy {
  revels: Revel[] = [];
  filteredRevels: Revel[] = [];
  searchQuery: string = '';
  currentUserIsAdmin: boolean = false;
  displayedColumns: string[] = []; // Initialize as empty
  isHandset$!: Observable<boolean>;
  private destroy$ = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private revelService: RevelService,
    private authService: AuthService,
    private responsive: ResponsiveService
  ) {}

  ngOnInit(): void {
    this.loadRevels();
    this.currentUserIsAdmin = this.authService.isCurrentUserAdmin();
    const desktopAdminCols = ['name', 'location', 'date', 'actions'];
    const desktopUserCols = ['name', 'location', 'date'];
    // Default for initial render
    this.displayedColumns = this.currentUserIsAdmin ? desktopAdminCols : desktopUserCols;

    // Responsive columns: handset shows only Name + Date
    this.isHandset$ = this.responsive.isHandset$;
    this.isHandset$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isHandset => {
        if (isHandset) {
          this.displayedColumns = ['name', 'date'];
        } else {
          this.displayedColumns = this.currentUserIsAdmin ? desktopAdminCols : desktopUserCols;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadRevels(): void {
    this.revelService.getRevels().subscribe(data => {
      this.revels = data.sort((a, b) => new Date(a.revelDate).getTime() - new Date(b.revelDate).getTime()); // Sort by Date
      this.filteredRevels = this.revels;
    });
  }

  searchRevels(): void {
    const query = this.searchQuery.toLowerCase();
    this.filteredRevels = this.revels.filter(revel =>
      (revel.revelName?.toLowerCase().includes(query) || // Safely handle undefined revelName
      revel.revelLocation?.toLowerCase().includes(query)) // Safely handle undefined revelLocation
    );
  }

  viewRevel(revel: Revel): void {
    this.dialog.open(RevelDialogComponent, {
      data: { revel, viewMode: true },
      width: '1000px',
      maxWidth: '90vw'
    });
  }

  editRevel(revel: Revel): void {
    const dialogRef = this.dialog.open(RevelDialogComponent, {
      data: { revel, viewMode: false },
      width: '1000px',
      maxWidth: '90vw'
    });

    dialogRef.afterClosed().subscribe((updatedRevel: Revel | null) => {
      if (updatedRevel) {
        this.revelService.updateRevel(updatedRevel.id, updatedRevel).subscribe(() => {
          this.loadRevels(); // Reload the list of revels after saving
        });
      }
    });
  }

  addRevel(): void {
    const dialogRef = this.dialog.open(RevelDialogComponent, {
      data: { revel: null, viewMode: false },
      width: '1000px',
      maxWidth: '90vw'
    });

    dialogRef.afterClosed().subscribe((newRevel: Revel | null) => {
      if (newRevel) {
        this.revelService.createRevel(newRevel).subscribe(() => {
          this.loadRevels(); // Reload the list of revels after saving
        });
      }
    });
  }

  deleteRevel(revelId: number): void {
    if (confirm('Are you sure you want to delete this revel?')) {
      this.revelService.deleteRevel(revelId).subscribe(() => {
        this.loadRevels();
      });
    }
  }
}
